
import { booleanAttribute, Component, ElementRef, EventEmitter, forwardRef, HostListener, Inject, Input, numberAttribute, OnInit, Optional, Output, PLATFORM_ID, Renderer2, ViewChild } from '@angular/core';
import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';
import { UploadFile } from '../upload-file';
import { UploadSetting } from '../upload-setting';
import { Observable, EMPTY } from 'rxjs';
import { HttpEvent, HttpEventType } from '@angular/common/http';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { UPLOAD_ASSETS, uploadAssetsDefaultConfig } from '../upload.module';
import { loadLazyCssFile } from '@techmore/global';

/**
 * 檔案上傳
 */
@Component({
  selector: 'tm-upload',
  templateUrl: './upload.component.html',
  styleUrls: ['./upload.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UploadComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => UploadComponent),
      multi: true,
    }
  ]
})
export class UploadComponent implements OnInit, ControlValueAccessor, Validator {
  /** 載入插件的方法 */
  static loadPlugin(document: Document, renderer: Renderer2, parentEl: Element, assetsConfig: { styles: string[]; }) {
    assetsConfig.styles.forEach(st => {
      const sta = st.split('/');
      const stName = sta[sta.length - 1];
      loadLazyCssFile(document, renderer, parentEl, stName, st);
    });
  }
  /** 是否渲染端為瀏覽器 */
  protected isBrowser: boolean;
  @ViewChild('fileInput', { static: false }) fileInputRef?: ElementRef;
  files: Array<UploadFile> = [];
  /** 是否正在上傳中 */
  loading = false;
  /** 檔案上傳設定 */
  @Input() uploadSetting?: UploadSetting;
  /** 上傳檔案的類型 */
  @Input({ required: true }) type?: string;
  /** 表單控件名 */
  @Input({required: true}) name?: string;
  /** 是否為單檔上傳 */
  @Input({transform: booleanAttribute}) single = false;
  /** 最少須上傳的數量 */
  @Input({transform: numberAttribute}) require?: number;
  /** 是否為可編輯狀態 */
  @Input({transform: booleanAttribute}) disabled = false;
  @Input() label: {
    uploadType: string;
    dropAreaText: string;
  } = {
      uploadType: '上傳格式:',
      dropAreaText: '拖曳檔案至此範圍內'
    };
  /** 是否顯示副檔名列，預設為顯示 */
  @Input({transform: booleanAttribute}) showFileType = true;
  /** 檔案上傳失敗事件 */
  @Output() uploadFailedEvent = new EventEmitter<any>();
  /**
   * 上傳檔案到到服務器
   * @param formData 檔案數據
   * @param type 檔案類型
   * @returns 上傳到服務器後的回傳值
   */
  @Input() upload2Serv = (type: string, formData: FormData): Observable<HttpEvent<any>> => EMPTY;
  propagateChange = (_: any) => { };
  touch = (_: any) => { };

  constructor(
    protected el: ElementRef,
    @Inject(PLATFORM_ID) protected platformId: any,
    @Inject(DOCUMENT) protected document: any,
    @Optional() @Inject(UPLOAD_ASSETS) protected assets: any,
    protected render: Renderer2,
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    }

  ngOnInit() {
    if (!this.type) {
      throw new Error('Need Type!');
    }
  }

  ngAfterViewInit() {
    if (this.isBrowser) {
      UploadComponent.loadPlugin(this.document, this.render, this.el.nativeElement, this.assets || uploadAssetsDefaultConfig);
    }
  }

  writeValue(obj: any): void {
    if (obj) {
      this.files = obj;
      this.sortFiles();
    } else if (this.files && this.files.length > 0) {
      this.files = [];
      this.propagateChange(this.files);
    }
  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.touch = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled ? true : false;
  }

  /**
   * 表單驗證
   * @param c 表單控件
   */
  validate(c: AbstractControl): ValidationErrors | null {
    if (this.require && this.files.length < this.require) {
      return { fileUploader: this.require };
    }
    return null;
  }

  @HostListener('click', ['$event'])
  onClick($event: Event) {
    if (!this.disabled) {
      this.fileInputRef?.nativeElement.click();
      this.touch('');
    }
  }

  @HostListener('dragover', ['$event'])
  onDragover($event: Event) {
    $event.preventDefault();
  }

  @HostListener('drop', ['$event'])
  onDrop($event: DragEvent) {
    $event.preventDefault();
    if (!this.disabled) {
      this.touch('');
      if ($event.dataTransfer) this.upload($event.dataTransfer.files);
    }
  }

  /** 檔案上傳輸入控件的資料改變 */
  fileInputOnChange($event: Event) {
    $event.stopPropagation();
    if (this.fileInputRef) {
    this.upload(this.fileInputRef.nativeElement.files);
    this.fileInputRef.nativeElement.value = '';
  }
  }

  /** 上傳檔案 */
  upload(files: FileList) {
    const length = files.length;

    for (let i = 0; i < length; i++) {
      if (!this.checkExt(files[i].name.split('.').pop())) {
        this.uploadFailedEvent.emit({ code: 'E1', msg: 'wrong format' });
        continue;
      }
      if (!this.checkSize(files[i].size)) {
        this.uploadFailedEvent.emit({ code: 'E2', msg: 'wrong size' });
        continue;
      }

      const formData = new FormData();
      formData.append('qqfile', files[i]);

      this.onUploadStart();

      const file: UploadFile = {
        name: files[i].name,
        progress: 0
      };
      if (files[i].size) {
        file.size = files[i].size;
      }
      let fileIndex: number;

      this.upload2Serv((this.type as any), formData).subscribe((event: HttpEvent<any>) => {
        switch (event.type) {
          case HttpEventType.Sent:
            if (this.single) {
              this.files.length = 0;
            }
            this.files.push(file);
            fileIndex = this.files.length - 1;
            break;
          case HttpEventType.UploadProgress:
            file.progress = event.total ? Math.round(event.loaded / event.total* 100) : 0;
            break;
          case HttpEventType.Response:
            this.onUploadFinish(file, event.body);
        }
      }, error => this.onUploadFailed(error, file, fileIndex));
    }
  }

  /**
   * 檔案上傳到服務器完畢
   * @param file 上傳的檔案
   */
  onUploadFinish(file: UploadFile, uploadFeedBack: any) {
    file.name = uploadFeedBack.name;
    file.fileid = uploadFeedBack.fileid;
    file.thumbnailUrl = uploadFeedBack.thumbnailUrl;
    file.transform = uploadFeedBack.transform;
    this.sortFiles();
    this.propagateChange(this.files);
    this.triggerDomChangeEvent();
    this.onUploadFinsihed();
  }

  /**
   * 檔案上傳到服務器失敗
   * @param error 錯誤
   * @param file 上傳失敗的檔案
   * @param fileIndex 在檔案列表中的索引
   */
  onUploadFailed(error: any, file: UploadFile, fileIndex: number) {
    this.uploadFailedEvent.emit({ code: 'E3', msg: error, fileName: file.name });
    this.onUploadFinsihed();
    this.files.splice(fileIndex, 1);
  }

  /** 檢查檔案類型 */
  checkExt(ext: string | undefined): boolean {
    if (ext === null || ext === undefined) {
      return false;
    }
    if (!this.uploadSetting?.type || this.uploadSetting?.type.length === 0) {
      return true;
    }

    return this.uploadSetting? this.uploadSetting.type.findIndex((val) => ext.toLocaleLowerCase() === val.toLocaleLowerCase()) >= 0 : false;
  }

  /** 檢查檔案大小 */
  checkSize(size: number): boolean {
    if (size === null || size === undefined) {
      return false;
    }

    return this.uploadSetting && this.uploadSetting.size ? this.uploadSetting.size >= size : false;
  }

  deleteFile($event: Event, file: UploadFile) {
    $event.preventDefault();
    this.stopPropagation($event);
    const index = this.files.findIndex((val) => val === file);
    this.files.splice(index, 1);
    this.propagateChange(this.files);
    this.triggerDomChangeEvent();
  }

  /** 停止冒泡 */
  stopPropagation($event: Event) {
    $event.stopPropagation();
  }

  /** 檔案上傳開始 */
  onUploadStart() {
    this.loading = true;
  }

  /** 檔案上傳結束 */
  onUploadFinsihed() {
    this.loading = false;
  }

  /** 按照檔名排序(升序) */
  sortFiles() {
    this.files = this.files.sort((a, b) => {
      if (a.name && b.name) {
        return a.name.toLocaleLowerCase() > b.name.toLocaleLowerCase() ? 1 : -1;
      } else { return -1; }
    });
  }

  /** 檔案上傳後要手動觸發原生change事件 */
  triggerDomChangeEvent() {
    let event: Event;
    if (typeof (Event) === 'function') {
      event = new Event('change', { bubbles: true, cancelable: true });
    } else {
      event = document.createEvent('Event');
      event.initEvent('change', true, true);
    }

    this.el.nativeElement.dispatchEvent(event);
  }
}
